import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import officeSpaceService from '../services/officeSpaceService';

export const useOfficeSpaceStore = create(
    persist(
        (set, get) => ({
            officeSpaces: [],
            selectedOfficeSpace: null,
            // Only the id is persisted; selectedOfficeSpace is re-resolved from the
            // fetched list on load (see fetchOfficeSpaces).
            selectedOfficeSpaceId: null,
            isLoading: false,
            error: null,

            // Fetch all office spaces for current user
            fetchOfficeSpaces: async () => {
                set({ isLoading: true, error: null });
                try {
                    const response = await officeSpaceService.getUserOfficeSpaces();
                    const officeSpaces = response.data || [];

                    // Always re-resolve the selection from the freshly fetched list so it
                    // points at a real, current object. Persistence only stores an id, so
                    // a stale/cross-environment id can't override the correct default —
                    // fall back to the first (default) office space when it no longer matches.
                    const persistedId = get().selectedOfficeSpaceId;
                    const resolved =
                        officeSpaces.find((os) => os.id === persistedId) ||
                        officeSpaces[0] ||
                        null;

                    set({
                        officeSpaces,
                        selectedOfficeSpace: resolved,
                        selectedOfficeSpaceId: resolved?.id ?? null,
                        isLoading: false,
                    });

                    return officeSpaces;
                } catch (error) {
                    set({ isLoading: false, error: error.message });
                    throw error;
                }
            },

            // Create new office space
            createOfficeSpace: async (data) => {
                set({ isLoading: true, error: null });
                try {
                    const response = await officeSpaceService.createOfficeSpace(data);
                    const newOfficeSpace = response.data;

                    set((state) => ({
                        officeSpaces: [...state.officeSpaces, newOfficeSpace],
                        isLoading: false,
                    }));

                    return newOfficeSpace;
                } catch (error) {
                    set({ isLoading: false, error: error.message });
                    throw error;
                }
            },

            // Update office space
            updateOfficeSpace: async (id, data) => {
                set({ isLoading: true, error: null });
                try {
                    const response = await officeSpaceService.updateOfficeSpace(id, data);
                    const updatedOfficeSpace = response.data;

                    set((state) => ({
                        officeSpaces: state.officeSpaces.map((os) =>
                            os.id === id ? updatedOfficeSpace : os
                        ),
                        selectedOfficeSpace:
                            state.selectedOfficeSpace?.id === id
                                ? updatedOfficeSpace
                                : state.selectedOfficeSpace,
                        isLoading: false,
                    }));

                    // `profileCompletionBonus` (a sibling of `data`) is set by the API when
                    // completing the shop just earned the one-time profile bonus.
                    return {
                        officeSpace: updatedOfficeSpace,
                        profileCompletionBonus: response.profileCompletionBonus === true,
                    };
                } catch (error) {
                    set({ isLoading: false, error: error.message });
                    throw error;
                }
            },

            // Delete office space
            deleteOfficeSpace: async (id) => {
                set({ isLoading: true, error: null });
                try {
                    await officeSpaceService.deleteOfficeSpace(id);

                    set((state) => {
                        const updatedOfficeSpaces = state.officeSpaces.filter(
                            (os) => os.id !== id
                        );
                        const nextSelected =
                            state.selectedOfficeSpace?.id === id
                                ? updatedOfficeSpaces[0] || null
                                : state.selectedOfficeSpace;

                        return {
                            officeSpaces: updatedOfficeSpaces,
                            selectedOfficeSpace: nextSelected,
                            selectedOfficeSpaceId: nextSelected?.id ?? null,
                            isLoading: false,
                        };
                    });

                    return { success: true };
                } catch (error) {
                    set({ isLoading: false, error: error.message });
                    throw error;
                }
            },

            // Select office space
            selectOfficeSpace: (officeSpace) => {
                set({
                    selectedOfficeSpace: officeSpace,
                    selectedOfficeSpaceId: officeSpace?.id ?? null,
                });
            },

            // Search users by phone
            searchUsersByPhone: async (phone) => {
                try {
                    const response = await officeSpaceService.searchUsersByPhone(phone);
                    return response.data || [];
                } catch (error) {
                    throw error;
                }
            },

            // Add operator to office space
            addOperator: async (officeSpaceId, operatorId) => {
                set({ isLoading: true, error: null });
                try {
                    await officeSpaceService.addOperator(officeSpaceId, operatorId);

                    // Refresh office spaces to get updated operator list
                    await get().fetchOfficeSpaces();

                    set({ isLoading: false });
                    return { success: true };
                } catch (error) {
                    set({ isLoading: false, error: error.message });
                    throw error;
                }
            },

            // Owner sets how an operator is paid
            setOperatorWage: async (officeSpaceId, operatorId, wage) => {
                const res = await officeSpaceService.setOperatorWage(officeSpaceId, operatorId, wage);
                await get().fetchOfficeSpaces();
                return res;
            },

            // Owner creates a brand-new operator (phone verified by SMS code)
            sendOperatorCreateOtp: async (officeSpaceId, phone) => {
                return officeSpaceService.sendOperatorCreateOtp(officeSpaceId, phone);
            },
            createOperator: async (officeSpaceId, payload) => {
                const res = await officeSpaceService.createOperator(officeSpaceId, payload);
                // Refresh so the new operator shows up in the list
                await get().fetchOfficeSpaces();
                return res;
            },

            // Remove operator from office space
            removeOperator: async (officeSpaceId, operatorId) => {
                set({ isLoading: true, error: null });
                try {
                    await officeSpaceService.removeOperator(officeSpaceId, operatorId);

                    // Refresh office spaces to get updated operator list
                    await get().fetchOfficeSpaces();

                    set({ isLoading: false });
                    return { success: true };
                } catch (error) {
                    set({ isLoading: false, error: error.message });
                    throw error;
                }
            },

            // Leave office space (operator only)
            leaveOfficeSpace: async (officeSpaceId) => {
                set({ isLoading: true, error: null });
                try {
                    await officeSpaceService.leaveOfficeSpace(officeSpaceId);

                    // Refresh office spaces
                    await get().fetchOfficeSpaces();

                    set({ isLoading: false });
                    return { success: true };
                } catch (error) {
                    set({ isLoading: false, error: error.message });
                    throw error;
                }
            },

            // Clear error
            clearError: () => set({ error: null }),

            // Reset store
            reset: () =>
                set({
                    officeSpaces: [],
                    selectedOfficeSpace: null,
                    selectedOfficeSpaceId: null,
                    isLoading: false,
                    error: null,
                }),
        }),
        {
            name: 'office-space-storage',
            // Bumped to 1: previously the whole selectedOfficeSpace object was
            // persisted, so a stale/cross-environment id could pin the app to the
            // wrong (or a non-existent) shop. Now only the id is stored and the
            // object is re-resolved from the server list on every fetch.
            version: 1,
            migrate: (persisted) => {
                // Drop the legacy persisted object; keep just its id (if any).
                const legacyId = persisted?.selectedOfficeSpace?.id ?? null;
                return { selectedOfficeSpaceId: persisted?.selectedOfficeSpaceId ?? legacyId };
            },
            partialize: (state) => ({
                selectedOfficeSpaceId: state.selectedOfficeSpaceId,
            }),
        }
    )
);
